import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import TagForm from "./TagForm";
import { Plus, Search } from "lucide-react";
import TagIcon from "../../assets/tag-icon.svg";
import DeleteIcon from "../../assets/delete-modal-icon.svg";
import EyeIcon from "../../assets/icons/EyeIcon";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon";
import NoDataIcon from "../../assets/icons/NoDataIcon";
import "../../style/productTags.css";
import EditIcon from "../../assets/icons/EditIcon";
import { tagAPI } from "../../services";
import { handleFormattedValidationErrors } from "../../utils";

const ProductTags = () => {
  const navigate = useNavigate();
  const { isPartsURL, isAccessoriesURL, isGodownURL } = useSelector((state) => state.domain);
  const [tags, setTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingTag, setDeletingTag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Load tags from API
  const loadTags = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm,
        page: currentPage,
        page_size: itemsPerPage,
      };
      const response = await tagAPI.getTagsList(params);
      const formattedTags = response.results.map(tag => ({
        id: tag.id,
        name: tag.identifier,
        description: tag.description,
        product_count: tag.total_available_qty || 0,
        date: new Date(tag.created_at).toLocaleDateString('en-GB'),
        image: tag.image,
      })).reverse();
      setTags(formattedTags);
      setTotalCount(response.count || 0);
      setTotalProducts(formattedTags.reduce((sum, tag) => sum + tag.product_count, 0));
    } catch (error) {
      console.error("Error loading tags:", error);
      toast.error("Failed to load tags");
    } finally {
      setLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(searchQuery);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Load tags on mount and when search or page changes
  useEffect(() => {
    loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage]);

  const handleAddTag = async (data) => {
    try {
      setLoading(true);
      await tagAPI.createTag(data);
      await loadTags();
      setShowForm(false);
      toast.success("Tag created successfully!");
    } catch (error) {
      console.error("Error creating tag:", error);
      handleFormattedValidationErrors(error, "Failed to create tag. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditTag = async (data) => {
    try {
      setLoading(true);
      await tagAPI.updateTag(editingTag.id, data);
      await loadTags();
      setShowForm(false);
      setEditingTag(null);
      toast.success("Tag updated successfully!");
    } catch (error) {
      console.error("Error updating tag:", error);
      handleFormattedValidationErrors(error, "Failed to update tag. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = async (id) => {
    try {
      setLoading(true);
      await tagAPI.deleteTag(id);
      await loadTags();
      setShowDeleteModal(false);
      setDeletingTag(null);
      toast.success("Tag deleted successfully!");
    } catch (error) {
      console.error("Error deleting tag:", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to delete tag. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openEditForm = (tag) => {
    setEditingTag(tag);
    setShowForm(true);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);



  return (
    <div className="card rounded-4 p-4 border-0 tags-container tags-layout">
      <div className="tags-top">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h2 className="mrph-txt size-22 weight-600 color-text-medium mb-1">
              Tags
            </h2>
            <p className="mrph-txt size-14 weight-400 color-text-semi-regular">
              Manage your product tags
            </p>
          </div>
          <button
            className="add-brand-btn-godown"
            onClick={() => setShowForm(true)}
          >
            <Plus className="add-brand-icon" /> Add Tag
          </button>
        </div>

        <div className="row mb-4">
          {/* summary cards */}
          <div className="col-md-4">
            <div
              className="card mrph-border border brands-summary-card"
              style={{ borderRadius: "10px", height: "92px" }}
            >
              <div className="card-body d-flex align-items-start justify-content-between">
                <div className="d-flex flex-column align-items-start justify-content-center gap-2">
                  <h6 className="mrph-txt size-14 weight-400 color-text-semi-regular mb-0">
                    Total Tags
                  </h6>
                  <h4 className="mrph-txt size-22 weight-600 color-text-medium mb-0">
                    {loading ? (
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      totalCount.toLocaleString()
                    )}
                  </h4>
                </div>
                <img src={TagIcon} alt="Total Tags" />
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div
              className="card mrph-border border brands-summary-card"
              style={{ borderRadius: "10px", height: "92px" }}
            >
              <div className="card-body d-flex align-items-start justify-content-between">
                <div className="d-flex flex-column align-items-start justify-content-center gap-2">
                  <h6 className="mrph-txt size-14 weight-400 color-text-semi-regular mb-0">
                    Total Products
                  </h6>
                  <h4 className="mrph-txt size-22 weight-600 color-text-medium mb-0">
                    {loading ? (
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      totalProducts.toLocaleString()
                    )}
                  </h4>
                </div>
                <img src={TagIcon} alt="Total Products" />
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center mb-3 gap-3">
          <div className="position-relative flex-grow-1">
            <input
              type="text"
              className="brand-search-input-godown text-search"
              placeholder="Search tags by name or description"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Search
              size={16}
              className="position-absolute top-50 start-0 translate-middle-y ms-2"
              style={{ color: "#8C959F" }}
            />
          </div>
        </div>
      </div>

      <div className="tags-table-container tags-table-wrapper">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <table className="tags-table">
            <thead style={{ backgroundColor: "#F6F8FA" }}>
              <tr>
                <th>TAG</th>
                <th>DESCRIPTION</th>
                <th>QTY.</th>
                <th>DATE</th>
                <th style={{ textAlign: "center" }}>ACTION</th>
              </tr>
            </thead>
            <tbody className="tags-tbody">
              {tags.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-products-message">
                    <div style={{ marginBottom: "4rem", marginTop: "4rem" }}>
                      <NoDataIcon />
                      <h3 style={{ marginTop: "2rem" }}>No tags found</h3>
                    </div>
                  </td>
                </tr>
              ) : (
                tags.map((tag) => (
                  <tr
                    key={tag.id}
                    className="mrph-txt size-14 color-text-regular"
                    style={{ height: "60px" }}
                  >
                    <td className="px-3">
                      <div className="d-flex align-items-center gap-3">
                        <span>{tag.name}</span>
                      </div>
                    </td>
                    <td className="px-3">
                      <span
                        className="tags-description-text"
                        title={tag.description || "-"}
                      >
                        {tag.description || "-"}
                      </span>
                    </td>
                    <td className="px-3">
                      {tag.product_count?.toLocaleString() || 0}
                    </td>
                    <td className="px-3">{tag.date}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn-godown view"
                          onClick={() => openEditForm(tag)}
                          title="Edit"
                        >
                          <EditIcon fill={isGodownURL ? "var(--primary-color-godown)" : "#3b82f6"} />
                        </button>
                        <button
                          className="action-btn-godown view"
                          onClick={() => navigate(`/tags/${tag.id}`)}
                          title="View"
                        >
                          <EyeIcon fill={isGodownURL ? "var(--primary-color-godown)" : "#3b82f6"} />
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => {
                            setDeletingTag(tag);
                            setShowDeleteModal(true);
                          }}
                          title="Delete"
                        >
                          <RedDeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="tags-pagination-sticky">
          <div className="pagination-container">
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            <div className="pagination-controls">
              <button
                className={`${isPartsURL
                  ? "pagination-btn-parts"
                  : isAccessoriesURL
                    ? "pagination-btn-acc"
                    : isGodownURL
                      ? "pagination-btn-godown"
                    : "pagination-btn"
                  } ${currentPage === 1 ? 'disabled' : ''}`}
                onClick={handlePrevious}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <button
                className={`${isPartsURL
                  ? "pagination-btn-next-parts"
                  : isAccessoriesURL
                    ? "pagination-btn-next-acc"
                    : isGodownURL
                      ? "pagination-btn-next-godown"
                    : "pagination-btn-next"
                  } ${currentPage === totalPages ? 'disabled' : ''}`}
                onClick={handleNext}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <TagForm
          tag={editingTag}
          onSave={editingTag ? handleEditTag : handleAddTag}
          onCancel={() => {
            setShowForm(false);
            setEditingTag(null);
          }}
        />
      )}

      {showDeleteModal && deletingTag && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <div className="modal-icon">
              <img src={DeleteIcon} alt="Delete" />
            </div>
            <h3 className="modal-title">Delete Tag</h3>
            <p className="modal-message">
              Are you sure you want to delete tag <b>{deletingTag.name}</b>?
              <br />
              This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                className="modal-cancel-btn"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="modal-delete-btn"
                onClick={() => handleDeleteTag(deletingTag.id)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductTags;
